// Product option modal — Unit / alternative-unit helpers: fractions, UOM types, conversion labels
// and the price of a line sold in an alternative unit.
import {
  firstNumber,
  getVariantServiceItemGroupList,
  getVariantServiceItemList,
  normalizeId,
} from './normalize';

export function toFraction(n) {
  const d = 1000;
  let a = Math.round(n * d),
    b = d;
  while (b) [a, b] = [b, a % b];
  const num = Math.round(n * d) / a;
  const den = d / a;
  return den === 1 ? `${num}` : `${num}/${den}`;
}

export function formatDecimalQuantity(value) {
  if (!Number.isFinite(value)) return '0';
  return Number(value.toFixed(3)).toString();
}

export function getResolvedUomType(unitDetails) {
  const type = String(unitDetails?.uom_type ?? unitDetails?.uomType ?? '').toUpperCase();
  const resolveType = String(
    unitDetails?.resolve_type ?? unitDetails?.resolveType ?? '',
  ).toUpperCase();

  if (type === 'PORTION' && (resolveType === 'WEIGHT' || resolveType === 'VOLUME')) {
    return resolveType;
  }

  return type;
}

export function isResolvedPortionUnit(unitDetails) {
  const type = String(unitDetails?.uom_type ?? unitDetails?.uomType ?? '').toUpperCase();
  const resolveType = String(
    unitDetails?.resolve_type ?? unitDetails?.resolveType ?? '',
  ).toUpperCase();

  return type === 'PORTION' && (resolveType === 'WEIGHT' || resolveType === 'VOLUME');
}

export function getMetricUnitFactor(unitDetails) {
  const type = getResolvedUomType(unitDetails);
  const code = String(unitDetails?.short_code ?? unitDetails?.name ?? '').toLowerCase();

  if (type === 'WEIGHT') {
    if (code === 'g' || code === 'gram' || code === 'grams') return 0.001;
    if (code === 'mg' || code === 'milligram' || code === 'milligrams') return 0.000001;
    if (code === 'kg' || code === 'kilogram' || code === 'kilograms') return 1;
  }

  if (type === 'VOLUME') {
    if (code === 'ml' || code === 'milliliter' || code === 'milliliters') return 0.001;
    if (code === 'l' || code === 'liter' || code === 'liters') return 1;
    if (code === 'ltr' || code === 'litre' || code === 'litres') return 1;
  }

  return firstNumber(unitDetails?.conversion_factor);
}

export function formatAlternativeUnitConversion(conversion, rate) {
  const altType = getResolvedUomType(conversion?.unit_details);
  const baseType = getResolvedUomType(conversion?.uom_details);
  const isResolvedPortion = isResolvedPortionUnit(conversion?.unit_details);
  const altFactor = getMetricUnitFactor(conversion?.unit_details);
  const baseFactor = getMetricUnitFactor(conversion?.uom_details);
  const shouldUsePrecision =
    !isResolvedPortion &&
    (altType === 'WEIGHT' || altType === 'VOLUME') &&
    altType === baseType &&
    altFactor &&
    baseFactor;

  if (isResolvedPortion && (altType === 'WEIGHT' || altType === 'VOLUME')) {
    return {
      quantity: formatDecimalQuantity(rate),
      unitName: conversion?.uom_details?.name || conversion?.uom || 'Unit',
    };
  }

  if (shouldUsePrecision) {
    return {
      quantity: formatDecimalQuantity((rate * baseFactor) / altFactor),
      unitName: conversion?.unit_details?.name || conversion?.unit || 'Unit',
    };
  }

  return {
    quantity: toFraction(rate),
    unitName: conversion?.uom_details?.name || conversion?.uom || 'PCS',
  };
}

// Numeric twin of formatAlternativeUnitConversion. Needed to rank packs by unit
// rate so the cheapest one can be flagged, and to render the "per base unit"
// line. Returns undefined when the pack has no usable quantity, which
// suppresses the rate line rather than printing a divide-by-zero.
export function getAlternativeUnitQuantity(conversion, rate) {
  const altType = String(conversion?.unit_details?.uom_type ?? '').toUpperCase();
  const baseType = String(conversion?.uom_details?.uom_type ?? '').toUpperCase();
  const altFactor = getMetricUnitFactor(conversion?.unit_details);
  const baseFactor = getMetricUnitFactor(conversion?.uom_details);

  if (
    (altType === 'WEIGHT' || altType === 'VOLUME') &&
    altType === baseType &&
    altFactor &&
    baseFactor
  ) {
    const quantity = (rate * baseFactor) / altFactor;
    return Number.isFinite(quantity) && quantity > 0 ? quantity : undefined;
  }

  return Number.isFinite(rate) && rate > 0 ? rate : undefined;
}

export function shouldHideAlternativeUnitName(conversion) {
  const altType = String(conversion?.unit_details?.uom_type ?? '').toUpperCase();
  return altType === 'WEIGHT' || altType === 'VOLUME';
}

export function getAlternativeUnitSelectionKey(conversion, index) {
  return [
    conversion?.unit,
    conversion?.uom,
    conversion?.conversion_rate ?? conversion?.conversionRate,
    conversion?.selling_price ?? conversion?.sellingPrice ?? conversion?.price,
    conversion?.barcode,
    index,
  ]
    .map((part) => String(part ?? ''))
    .join('|');
}

export function getSelectedAlternativeUomId(conversion) {
  return (
    conversion?.uom_id ??
    conversion?.uomId ??
    conversion?.alternative_uom_id ??
    conversion?.alternativeUomId ??
    conversion?.id ??
    conversion?.unit_details?.uom_id ??
    conversion?.unitDetails?.uomId ??
    conversion?.unit ??
    null
  );
}

export function normalizeAlternativeUnitServiceOption(conversion, index) {
  const hasOwnServiceItems =
    getVariantServiceItemList(conversion).length > 0 ||
    getVariantServiceItemGroupList(conversion).length > 0;
  const id = normalizeId(getSelectedAlternativeUomId(conversion), `alternative-unit-${index}`);

  return {
    ...conversion,
    id,
    variant_id: id,
    name: conversion?.unit_details?.name ?? conversion?.unit ?? `Unit ${index + 1}`,
    __hasOwnServiceItems: hasOwnServiceItems,
  };
}

export function getLineAlternativeUnitDetails(line) {
  return (
    line?.alternativeUnitConversion ??
    line?.alternativeUnitDetails ??
    line?.alternative_unit_details ??
    line?.alternative_units_and_conversions?.[0] ??
    null
  );
}

export function sameValue(a, b) {
  if (a === undefined || a === null || a === '' || b === undefined || b === null || b === '') {
    return false;
  }
  return String(a) === String(b);
}

export function sameNumber(a, b) {
  const first = firstNumber(a);
  const second = firstNumber(b);
  if (first === undefined || second === undefined) return false;
  return Math.abs(first - second) < 0.0001;
}

export function isSameAlternativeUnitConversion(option, selectedDetails, line) {
  if (!option || !selectedDetails) return false;

  const selectedUomId = line?.selectedUomId ?? line?.selected_uom_id ?? line?.uom_id;
  if (sameValue(selectedUomId, getSelectedAlternativeUomId(option))) return true;

  const sameUnit = sameValue(option.unit, selectedDetails.unit);
  const sameBaseUom = sameValue(option.uom, selectedDetails.uom);
  const sameRate = sameNumber(
    option.conversion_rate ?? option.conversionRate,
    selectedDetails.conversion_rate ?? selectedDetails.conversionRate ?? line?.conversionRate,
  );
  const samePriceDelta = sameNumber(
    option.price_delta ?? option.priceDelta ?? 0,
    selectedDetails.price_delta ?? selectedDetails.priceDelta ?? 0,
  );

  return sameUnit && sameBaseUom && sameRate && samePriceDelta;
}

export function resolveAlternativeUnitPrice(conversion, basePrice) {
  if (!conversion) return firstNumber(basePrice, 0) ?? 0;
  const priceDelta = firstNumber(conversion?.price_delta, conversion?.priceDelta, 0) ?? 0;

  const explicitAltPrice = firstNumber(
    conversion?.selling_price,
    conversion?.sellingPrice,
    conversion?.price,
    conversion?.amount,
  );

  if (explicitAltPrice !== undefined && explicitAltPrice > 0) {
    return explicitAltPrice + priceDelta;
  }

  const conversionRate =
    firstNumber(conversion?.conversion_rate, conversion?.conversionRate, 1) ?? 1;
  return (firstNumber(basePrice, 0) ?? 0) * conversionRate + priceDelta;
}
